import { useGetEventQuery } from '@/state/queries/events/use-get-event.ts';
import { Box, Container, Fade, Stack, Tab, Tabs } from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import FixedNavigationBar from '@/components/common/fixed-top-bar';
import { DashboardTab } from './tabs/dashboard';
import { FlowsTab } from './tabs/flows';
import { useEventStore } from '@/state/stores/use-event-store';
import { useTabs } from '@/hooks/use-tabs';
import { useTranslations } from '@/i18n/use-translations';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import { normalizeEventFlowsResponse, useGetEventFlowsQuery } from '@/state/queries/flows/use-get-event-flows.ts';
import { useGetEventContributorsQuery } from '@/state/queries/contributors/use-get-event-contributors.ts';
import { useEventSessionsQuery } from '@/state/queries/sessions/use-event-sessions.ts';
import type { TranslationKey } from '@/i18n/translation-key.tsx';
import { SessionsTab } from '@/components/events/detail/tabs/sessions';
import { ContributorsTab } from '@/components/events/detail/tabs/contributors/contributors';
import { EVENT_SLOT_TYPE } from '@/types/dtos/events/event-slot-content.enum';
import { useGetProductsQuery } from '@/state/queries/orders/use-get-products.ts';
import { useProductsStore } from '@/state/stores/use-products-store.ts';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTheme } from '@mui/material';
import { ensureFlowGlobalHandlers } from '@/hooks/use-flow-socket';
import { useContributorSocketSync } from '@/hooks/use-contributor-socket-sync';
import { useEventPublicationSocketSync } from '@/hooks/use-event-publication-socket-sync';
import { useAuth } from '@clerk/clerk-react';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { alpha } from '@mui/material/styles';
import { useBreakpointDown } from '@/hooks/use-breakpoint';

const tabs = ['dashboard', 'flows', 'sessions', 'contributors'] as const;
export type TabType = (typeof tabs)[number];

export default function EventDetail() {
  const t = useTranslations();
  const { eventId } = useParams();
  useEventPublicationSocketSync(eventId);
  useContributorSocketSync();
  const { tabValue, tabIndex, handleTabChange } = useTabs<TabType>(tabs);
  const {
    data: event,
    isError: isEventError,
    error: eventError,
    isLoading: isEventLoading,
    normalized: normalizedEvent,
  } = useGetEventQuery(eventId);
  // Use origin field for API call to match backend expectations
  const eventIdForContributors = event?.origin
    ? typeof event.origin === 'string'
      ? event.origin
      : event.origin.id
    : event?.id;
  const { data: contributors } = useGetEventContributorsQuery(eventIdForContributors);
  const { data: flows } = useGetEventFlowsQuery(eventId);
  const { data: sessions, normalized: normalizedSessions } = useEventSessionsQuery({
    eventId,
    loadAllModes: true,
  });
  const { data: catalogProducts } = useGetProductsQuery();
  const navigate = useNavigate();

  const activeOrganization = useActiveOrganizationStore((s) => s.activeOrganization);
  const {
    setEvent,
    setEventPlan,
    setEventOrganize,
    setEventFlows,
    setEventFlowsPlan,
    setEventFlowsOrganize,
    setEventSessions,
    setEventSessionsPlan,
    setEventSessionsOrganize,
    setEventContributors,
    clearEventData,
  } = useEventStore();
  const setProducts = useProductsStore((s) => s.setProducts);
  const theme = useTheme();
  const isMobile = useBreakpointDown('sm');
  const { getToken } = useAuth();
  const eventMode = useEventMode();

  // Get background color based on event mode
  const pageBackgroundColor = useMemo(() => {
    if (eventMode === 'PERFORM') {
      return alpha(theme.palette.error.main, 0.05);
    }
    if (eventMode === 'ORGANIZE') {
      return alpha(theme.palette.secondary.main, 0.2);
    }
    // PLAN mode: normal background (no override)
    return undefined;
  }, [eventMode, theme]);

  const [activateContributorInlineAdding, setActivateContributorInlineAdding] = useState(false);

  useEffect(() => {
    clearEventData();
  }, [eventId, clearEventData]);

  useEffect(() => {
    if (event) {
      if (normalizedEvent?.eventPlan) {
        setEventPlan(normalizedEvent.eventPlan);
      }
      if (normalizedEvent?.eventOrganize) {
        setEventOrganize(normalizedEvent.eventOrganize);
      }
      setEvent(event);
      // Ensure global flow socket handlers are initialized early for real-time updates
      (async () => {
        const token = await getToken();
        await ensureFlowGlobalHandlers(token ?? undefined);
      })();
      if (!event.multiFlow && event?.flows) {
        const singleFlow = event.flows[0];
        if (singleFlow) {
          const eventId = event.origin ? (typeof event.origin === 'string' ? event.origin : event.origin.id) : event.id;
          // Find the eventSlot for this flow to preserve timing data
          const flowEventSlot = event.slots?.find(
            (slot) =>
              (slot.content === singleFlow.id || slot.content === singleFlow.origin) &&
              slot.contentType === EVENT_SLOT_TYPE.FLOW,
          );
          const eventSlotIdParam = flowEventSlot ? `&eventSlotId=${flowEventSlot.id}` : '';
          navigate(`/flow/${singleFlow.id}?contextType=event&contextId=${eventId}${eventSlotIdParam}`, {
            replace: true,
          });
        }
      }
    }
  }, [event, normalizedEvent, setEvent, setEventPlan, setEventOrganize, navigate, getToken]);
  useEffect(() => {
    if (flows && event) {
      const flowsWithSlot = flows.map((flow) => {
        return {
          ...flow,
          slot: event.slots?.find(
            (slot) =>
              (slot.content === flow.id || slot.content === flow.origin) && slot.contentType === EVENT_SLOT_TYPE.FLOW,
          ),
        };
      });
      const { planFlows, organizeFlows } = normalizeEventFlowsResponse(flowsWithSlot);
      if (planFlows.length > 0) {
        setEventFlowsPlan(planFlows);
      }
      if (organizeFlows.length > 0) {
        setEventFlowsOrganize(organizeFlows);
      }
      setEventFlows(planFlows.length > 0 ? planFlows : organizeFlows.length > 0 ? organizeFlows : flowsWithSlot);
      // Ensure global flow socket handlers are initialized for real-time updates
      (async () => {
        const token = await getToken();
        await ensureFlowGlobalHandlers(token ?? undefined);
      })();
    }
  }, [flows, event, setEventFlows, setEventFlowsPlan, setEventFlowsOrganize, getToken]);
  useEffect(() => {
    if (!sessions) return;
    const { planSessions, organizeSessions, preferredSessions } = normalizedSessions;
    if (planSessions.length > 0) {
      setEventSessionsPlan(planSessions);
    }
    if (organizeSessions.length > 0) {
      setEventSessionsOrganize(organizeSessions);
    }
    if (preferredSessions.length > 0) {
      setEventSessions(preferredSessions);
    }
  }, [sessions, normalizedSessions, setEventSessions, setEventSessionsPlan, setEventSessionsOrganize]);
  useEffect(() => {
    if (contributors) setEventContributors(contributors); // TODO: this has pagination for future
  }, [contributors, setEventContributors]);

  useEffect(() => {
    if (catalogProducts) {
      setProducts(catalogProducts);
    }
  }, [catalogProducts, setProducts]);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [tabValue]);

  // Reset the activate inline adding flag after it's been used
  useEffect(() => {
    if (activateContributorInlineAdding && tabValue === 'contributors') {
      // Use setTimeout to ensure the ContributorsTab has time to process the prop
      const timer = setTimeout(() => {
        setActivateContributorInlineAdding(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [activateContributorInlineAdding, tabValue]);

  // Handle event load error - navigate to organization events list
  useEffect(() => {
    if (isEventError && !isEventLoading && eventError && activeOrganization?.id) {
      navigate(`/organization/${activeOrganization.id}/events`, { replace: true });
    }
  }, [isEventError, isEventLoading, eventError, activeOrganization?.id, navigate]);

  const renderActiveTab = () => {
    switch (tabValue) {
      case 'dashboard':
        return <DashboardTab setActivateContributorInlineAdding={setActivateContributorInlineAdding} />;
      case 'flows':
        return <FlowsTab />;
      case 'sessions':
        return <SessionsTab />;
      case 'contributors':
        return <ContributorsTab activateInlineAdding={activateContributorInlineAdding} />;
      default:
        return <DashboardTab setActivateContributorInlineAdding={setActivateContributorInlineAdding} />;
    }
  };

  return (
    <Container
      sx={{
        'borderBottom': 0,
        'borderColor': 'divider',
        'bgcolor': pageBackgroundColor,
        // 100vh includes mobile browser chrome in ways that shift when the bar hides; dvh tracks the visible viewport
        'minHeight': '100vh',
        '@supports (height: 100dvh)': {
          minHeight: '100dvh',
        },
      }}
    >
      <FixedNavigationBar
        context="event"
        backTo={`/organization/${activeOrganization?.id}/events`}
        showBackButton={!isMobile}
      >
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          variant={isMobile ? 'fullWidth' : 'scrollable'}
          scrollButtons={isMobile ? false : 'auto'}
          allowScrollButtonsMobile={!isMobile}
          slots={
            isMobile
              ? undefined
              : {
                  startScrollButtonIcon: ChevronLeftIcon,
                  endScrollButtonIcon: ChevronRightIcon,
                }
          }
          sx={{
            'width': isMobile ? '100%' : 'calc(100% + 32px)',
            'mx': isMobile ? 0 : -2,
            'px': isMobile ? 0 : 2,
            'position': 'relative',
            '& .MuiTabs-scrollButtons': {
              width: '30px',
              margin: 0,
              padding: 0,
            },
            '& .MuiTabs-scrollButtons.Mui-disabled': {
              display: 'none',
            },
            '& .MuiTabs-startScrollButton': {
              position: 'absolute',
              left: 0,
            },
            '& .MuiTabs-endScrollButton': {
              position: 'absolute',
              right: 0,
            },
            '& .MuiTab-root': {
              'minWidth': 'fit-content',
              'paddingLeft': isMobile ? 1 : undefined,
              'paddingRight': isMobile ? 1 : undefined,
              '&:focus': { outline: 'none' },
              '&.Mui-focusVisible': { outline: 'none' },
            },
          }}
        >
          {tabs.map((tabOption) => (
            <Tab key={tabOption} label={t(`event.detail.${tabOption}.title` as TranslationKey)} />
          ))}
        </Tabs>
      </FixedNavigationBar>
      <Stack
        sx={{
          // Body padding only adds --fixed-topbar-min-height; reserve the measured bar (title + tabs) minus that.
          mt: 'calc(var(--fixed-navigation-bar-height) - (var(--fixed-topbar-visible) * var(--fixed-topbar-min-height)))',
        }}
        justifyContent="center"
        alignItems="center"
      >
        <Fade in={true} timeout={500}>
          <Box width="100%" sx={{ m: '0.8em 0' }}>
            {renderActiveTab()}
          </Box>
        </Fade>
      </Stack>
    </Container>
  );
}
